import { ReactElement, ReactNode, useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import {
  TextField as MuiTextField,
  InputProps,
  OutlinedTextFieldProps,
  InputAdornment,
  type TextFieldProps,
} from '@mui/material'
import { InfoTooltip } from '@ui/feedback'
import { Box } from '@ui/structure'
import FormControl from '../FormControl'
import NumberInput from '../NumberInput'

interface Props<F> extends Omit<OutlinedTextFieldProps, 'variant'> {
  variant?: TextFieldProps['variant']
  name: F extends object ? Extract<keyof F, string> : string
  placeholder?: string
  label?: string | ReactNode
  inputLabel?: string | ReactNode
  tooltip?: TooltipContents
  /** Render `tooltip` next to `inputLabel` instead of the outer FormControl label. */
  tooltipOnInputLabel?: boolean
  InputProps?: InputProps
  unitLabel?: string | ReactNode
  conversionFactor?: number
  'data-cy'?: string
  hideAdornment?: boolean
  /** Skip outer `FormControl` (e.g. Results2 support-grid inline fields with external labels). */
  hideFormControl?: boolean
}

const UnitConvertedField = <F,>({
  name,
  rows = 1,
  multiline = false,
  autoFocus = false,
  placeholder = '',
  disabled = false,
  label = '',
  inputLabel,
  InputProps = {},
  InputLabelProps,
  tooltip,
  tooltipOnInputLabel = false,
  unitLabel = 'kN',
  conversionFactor = 1000,
  'data-cy': dataCy,
  hideAdornment = false,
  hideFormControl = false,
  variant,
  ...textFieldProps
}: Props<F>): ReactElement => {
  const { control } = useFormContext()
  const {
    field: { onChange, onBlur, name: fieldName, ref },
    fieldState: { error },
  } = useController({ name, control })

  const value = useWatch({ name })

  const displayValue = useMemo(() => {
    return !value ? value : Number(value / conversionFactor)
  }, [conversionFactor, value])

  const showTooltipOnInputLabel = Boolean(tooltipOnInputLabel && tooltip && inputLabel)
  const muiInputLabel =
    showTooltipOnInputLabel && tooltip ? (
      <Box display="flex" alignItems="center" sx={{ gap: 0.25 }}>
        {inputLabel}
        <Box onMouseDown={event => event.preventDefault()}>
          <InfoTooltip text={tooltip.text} headline={tooltip.headline} flex />
        </Box>
      </Box>
    ) : (
      inputLabel
    )

  const textField = (
    <MuiTextField
      variant={variant ?? 'outlined'}
      onChange={event => {
        const eventValue = event.target.value
        const value = eventValue.length ? Number(eventValue) * conversionFactor : eventValue
        onChange(value)
      }}
      onBlur={onBlur}
      value={displayValue}
      type="text"
      placeholder={placeholder}
      autoFocus={autoFocus}
      name={fieldName}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      inputRef={ref}
      size="small"
      error={hideFormControl ? Boolean(error) : undefined}
      helperText={hideFormControl ? error?.message : undefined}
      FormHelperTextProps={hideFormControl ? { sx: { mx: 0, mt: 0.25, fontSize: 11 } } : undefined}
      InputProps={{
        // eslint-disable-next-line
        inputComponent: NumberInput as any,
        ...(hideAdornment
          ? {}
          : { endAdornment: <InputAdornment position="end">{unitLabel}</InputAdornment> }),
        ...InputProps,
      }}
      InputLabelProps={{
        ...(showTooltipOnInputLabel ? { sx: { pointerEvents: 'auto' } } : {}),
        ...InputLabelProps,
      }}
      label={muiInputLabel}
      inputProps={{
        step: 0.01,
        min: 0,
      }}
      data-cy={dataCy}
      {...textFieldProps}
    />
  )

  if (hideFormControl) {
    return textField
  }

  return (
    <FormControl
      label={!inputLabel ? label : undefined}
      error={error?.message}
      tooltip={showTooltipOnInputLabel ? undefined : tooltip}
      data-cy={dataCy}
    >
      {textField}
    </FormControl>
  )
}

export default UnitConvertedField
